import BaseBrowserStore from './base/base-browser-store.js'

/** One calendar year, in milliseconds (TTL for persisted partner data). */
const PARTNER_CODE_TTL_MS = 365 * 24 * 60 * 60 * 1000

const STORAGE_KEY = 'signup_payload'

/** Secret partner code that WEB blindly passes to API as `invitation_token`. */
const PARTNER_CODE_QUERY_PARAM = 'signup_code'

/** Partner slug to identify the partner to associate the user with. */
const PARTNER_SLUG_QUERY_PARAM = 'signup_location'

/**
 * @typedef {{ code: string, slug: string, expiresAt: number }} PartnerSignupPayload
 */

class PartnerStore extends BaseBrowserStore {
    constructor () {
        super('partner', {})
    }

    /**
     * @returns {Promise<PartnerSignupPayload | null>}
     */
    async _readPayload () {
        const raw = await this.get(STORAGE_KEY)
        if (raw == undefined || raw === '') {
            return null
        }
        try {
            const parsed = JSON.parse(raw)
            if (
                typeof parsed !== 'object'
                || parsed === null
                || typeof parsed.expiresAt !== 'number'
            ) {
                await this.remove(STORAGE_KEY)
                return null
            }
            if (Date.now() > parsed.expiresAt) {
                await this.remove(STORAGE_KEY)
                return null
            }
            const code = typeof parsed.code === 'string' ? parsed.code : null
            const slug = typeof parsed.slug === 'string' ? parsed.slug : null
            // We need both the code and the slug to be present
            if (!code || !slug) {
                await this.remove(STORAGE_KEY)
                return null
            }
            return {
                code,
                expiresAt: parsed.expiresAt,
                slug,
            }
        } catch {
            await this.remove(STORAGE_KEY)
            return null
        }
    }

    /**
     * @returns {Promise<void>}
     */
    async _writePayload (code, slug) {
        if (!code || !slug) {
            await this.remove(STORAGE_KEY)
            return
        }
        /** @type {PartnerSignupPayload} */
        const payload = {
            code,
            expiresAt: Date.now() + PARTNER_CODE_TTL_MS,
            slug,
        }
        await this.set(STORAGE_KEY, JSON.stringify(payload))
    }

    /**
     * @returns {Promise<string | null>} The stored partner invite code, or null if missing or expired.
     */
    async getPartnerCode () {
        const payload = await this._readPayload()
        return payload?.code ?? null
    }

    /**
     * @returns {Promise<string | null>} The stored partner slug for config/UI lookup, or null if missing or expired.
     */
    async getPartnerSlug () {
        const payload = await this._readPayload()
        return payload?.slug ?? null
    }

    /**
     * Persists the partner code and slug with a 1-year TTL.
     * @param {string} code
     * @param {string} slug
     * @returns {Promise<void>}
     */
    async setPartnerData (code, slug) {
        await this._writePayload(code, slug)
    }

    /**
     * @returns {Promise<void>}
     */
    async clearPartnerData () {
        await this.remove(STORAGE_KEY)
    }

    /**
     * Reads `partner_code` and `partner_slug` from the current URL, persists both,
     * then strips both params with replaceState (no full page reload).
     * Safe to call once at app startup.
     * @returns {Promise<void>}
     */
    async capturePartnerCodeFromUrl () {
        if (typeof window === 'undefined') {
            return
        }

        const url = new URL(window.location.href)
        const hasCodeParameter = url.searchParams.has(PARTNER_CODE_QUERY_PARAM)
        const hasSlugParameter = url.searchParams.has(PARTNER_SLUG_QUERY_PARAM)
        if (!hasCodeParameter || !hasSlugParameter) {
            return
        }

        const codeFromUrl = (url.searchParams.get(PARTNER_CODE_QUERY_PARAM) ?? '').trim()
        const slugFromUrl = (url.searchParams.get(PARTNER_SLUG_QUERY_PARAM) ?? '').trim()

        url.searchParams.delete(PARTNER_CODE_QUERY_PARAM)
        url.searchParams.delete(PARTNER_SLUG_QUERY_PARAM)
        window.history.replaceState({}, '', url.toString())

        if (!codeFromUrl || !slugFromUrl) {
            await this.clearPartnerData()
            return
        }
        // Write the new data to the store
        await this._writePayload(codeFromUrl, slugFromUrl)
    }
}

const partnerStore = new PartnerStore()
export default partnerStore
